import React, { forwardRef, useState, useEffect } from "react";
import cx from "classnames";
import styled from 'styled-components';
import { useSwipeable } from 'react-swipeable';
import { FaArrowLeft, FaArrowRight } from 'react-icons/fa'
import IBuildrProps from '../../utilities/IBuildrProps';
import { CardDeckProvider } from "../../context/CardDeckContext";
import CardTypeEnum from "../Card/CardTypeEnum";

interface ICardDeckProps extends IBuildrProps {
    cardType: CardTypeEnum;
    enableSwipe: boolean;
    enableCounter: boolean;
    enableArrows: boolean;
    enableOverflow: boolean;
    enableAccordion: boolean;
    children: any;
    cardClassName: string;
    desktopColumns: number;
    tabletColumns: number;
    mobileColumns: number;
    dataTestId?: string;
}

const CardDeckContainer = styled.div`
    .card-container:empty {
        min-height: 250px;
    }

    .card-counter {
        color: #000;
        font-family: "National2";
        font-size: 0.88889rem;
        font-style: normal;
        font-weight: 700;
        line-height: 1.55556rem;
        letter-spacing: 0.02667rem;
        text-transform: uppercase;
    }

    .card-arrows {
        margin-right: 0;
        margin-left: auto;
        .arrow-left {
            margin-right: 15px
        }
    }

    .btn-underline-dark:hover, .btn-underline-light:hover {
        font-weight: 500;
    }

    @media (max-width:575px) {
        .card.last-row-mobile {
            margin-bottom: 0 !important;
        }
    }

    @media (min-width:576px) and (max-width:991px) {
        .card.last-row-tablet {
            margin-bottom: 0 !important;
        }
    }

    @media (min-width: 992px) {
        .card.last-row-desktop {
            margin-bottom: 0 !important;
        }
    }
`;

const CarouselContainer = styled.div<{$pos, $numItems, $cardsGap, $desktopColumns, $tabletColumns, $mobileColumns, $enableOverflow}>`
    flex-flow: wrap;
    transition: none;
    transform: translateX(0);
    column-gap: ${(props) => props.$cardsGap}px;

    &.solidColorPhoto {
        max-width: ${({$desktopColumns, $cardsGap}) => {
            return `calc(328px * ${$desktopColumns} + (${$cardsGap}px * ${$desktopColumns - 1}) )`;
        }};
    }

    .card {
        width: ${({$desktopColumns , $cardsGap}) => {
            return `calc(100% / ${$desktopColumns} - (${$cardsGap}px * ${$desktopColumns - 1} / ${$desktopColumns}))`;
        }};
    }

    @media (max-width:991px) {
        // Card width for swipeable functionality based on columns
        --cardWidth: ${({$cardsGap, $enableOverflow, $tabletColumns}) => {
            return `calc(${$enableOverflow ? '90%' : '100%'} / ${$tabletColumns} - (${$cardsGap}px * ${$tabletColumns - 1} / ${$tabletColumns}))`;
        }};

        &.solidColorPhoto {
            max-width: ${({$tabletColumns, $cardsGap}) => {
                return `calc(328px * ${$tabletColumns} + (${$cardsGap}px * ${$tabletColumns - 1}) )`;
            }};
        }

        .card {
            width: ${({$tabletColumns, $cardsGap}) => {
                return `calc(100% / ${$tabletColumns} - (${$cardsGap}px * ${$tabletColumns - 1} / ${$tabletColumns}))`;
            }};
        }

        &.swipeable.enableTabletSwipe {
            flex-flow: row;
            transition: transform .5s ease;
            max-width: 100%;
            transform: ${(props) => {
                if (props.$pos > 0) {
                    const lastItem = (props.$pos === props.$numItems - 1);

                    if (props.$enableOverflow) {
                        return `translateX(calc(-${props.$pos} * (var(--cardWidth) + ${props.$cardsGap}px) + ${lastItem ? '10%' : '0%'}))`;
                    } else {
                        return `translateX(calc(-${props.$pos} * (var(--cardWidth) + ${props.$cardsGap}px)))`;

                    }
                }

                return `translateX(0)`;
            }};

            .card {
                margin: 0!important;
                width: 100%;
                max-width: 100%;
                flex: 1 0 var(--cardWidth);
            }
        }
    }

    @media (max-width:575px) {
        &.solidColorPhoto {
            max-width: 100%;
        }

        .card {
            width: ${({$mobileColumns, $cardsGap}) => {
                return `calc(100% / ${$mobileColumns} - (${$cardsGap}px * ${$mobileColumns - 1} / ${$mobileColumns}))`;
            }};
        }

        &.swipeable, &.swipeable.enableTabletSwipe {
            flex-flow: row;
            transition: transform .5s ease;
            transform: ${(props) => {
                if (props.$pos > 0) {
                    const lastItem = (props.$pos === props.$numItems - 1);

                    if (props.$enableOverflow) {
                        return `translateX(calc(-${props.$pos} * (90% + ${props.$cardsGap}px) + ${lastItem ? '10%' : '5%'}))`;
                    } else {
                        return `translateX(calc(-${props.$pos} * (100% + ${props.$cardsGap}px)))`;
                    }
                }

                return `translateX(0)`;
            }};

            .card {
                margin: 0!important;
                width: 100%;
                flex: ${({$enableOverflow}) => {
                    return `1 0 ${$enableOverflow ? 90 : 100}%`;
                }};
            }
        }
    }
`;

const NEXT = 'NEXT';
const PREV = 'PREV';

const CardDeck = forwardRef((props: ICardDeckProps, ref: any) => {
    const { cmsId, editorClassObj, className, children, cardType, enableCounter, enableArrows, enableOverflow, enableAccordion, cardClassName, desktopColumns = 3, tabletColumns = 2, mobileColumns = 1, dataTestId } = props;

    const [currentCard, setCurrentCard] = useState(0);
    const [totalCards, setTotalCards] = useState(children?.length || 0);

    const cardProps = { cardType, enableCounter, enableArrows, enableOverflow, enableAccordion, totalCards };
    const enableSwipe = !(enableAccordion && cardType === CardTypeEnum.SimpleText) && (enableCounter || enableArrows || enableOverflow);
    const enableTabletSwipe = cardType === CardTypeEnum.SolidColor || cardType === CardTypeEnum.SolidColorPhoto;

    const cardGap = cardType === CardTypeEnum.SimpleText ? 30 : 15;

    function formatNumber(number) {
        return String(number).padStart(2, '0');
    }

    const slide = (direction) => {
        const newPos = direction === NEXT ? currentCard + 1 : currentCard - 1;
        if (newPos >= 0 && newPos < totalCards) {
            setCurrentCard(newPos);
        }
      };

    const handlers = useSwipeable({
        onSwipedLeft: () => slide(NEXT),
        onSwipedRight: () => slide(PREV),
        swipeDuration: 500,
        preventScrollOnSwipe: true
    });

    const isLastRow = (index, total, pageSize) => {
        if (total <= pageSize)
            return true;
        if (total % pageSize === 0) {
            return index >= total - pageSize;
        }
        return index >= (Math.floor(total / pageSize)) * pageSize;
    }

    const getRowClass = (index, total) => {
        let rowClasses = [];
        if (enableSwipe || isLastRow(index, total, mobileColumns)) {
            rowClasses.push('last-row-mobile');
        }
        if ((enableSwipe && enableTabletSwipe) || isLastRow(index, total, tabletColumns)) {
            rowClasses.push('last-row-tablet');
        }
        if (isLastRow(index, total, desktopColumns)) {
            rowClasses.push('last-row-desktop');
        }
        return rowClasses.join(' ');
    }

    useEffect(() => {
        // Changing total cards amount in tablet with an offset from column number
        if(window.innerWidth >= 576 && window.innerWidth < 992) {
            const newTotal = children?.length - (tabletColumns - 1) ;
            setTotalCards(newTotal);
        }
    }, []);

    return (
        <CardDeckProvider {...cardProps}>
            <CardDeckContainer ref={ref} data-id={cmsId} className={cx(className, editorClassObj, "w-100")} data-test-id={dataTestId}>
                <div className={cx("d-flex card-container flex-wrap justify-content-center", {"overflow-hidden": !enableOverflow})} {...handlers}>
                    <CarouselContainer $pos={currentCard} $numItems={totalCards} className={cx("d-flex w-100", {'swipeable': enableSwipe, enableTabletSwipe,  'solidColorPhoto': cardType === CardTypeEnum.SolidColorPhoto})} $cardsGap={cardGap} $desktopColumns={desktopColumns} $tabletColumns={tabletColumns} $mobileColumns={mobileColumns} $enableOverflow={enableOverflow}>
                        {React.Children.map(children, (card, cardIndex) => {
                            return React.cloneElement(card, {
                                className: cx(cardClassName, card.props.className, getRowClass(cardIndex, children.length)),
                                cardIndex,
                            });
                        })}
                    </CarouselContainer>
                </div>
                <div className={cx("d-flex justify-content-between col-12")}>
                    {enableSwipe && enableCounter ? <span className={cx("card-counter d-sm-none")}>{`${formatNumber(currentCard + 1)}/${formatNumber(totalCards)}`}</span> : null}
                    {enableSwipe && enableArrows ? <span className={cx("card-arrows d-lg-none", {'d-sm-none': !enableTabletSwipe})}>
                        <span onClick={() => slide(PREV)} className="arrow-left">
                            <FaArrowLeft />
                        </span>
                        <span onClick={() => slide(NEXT)} className="arrow-right">
                            <FaArrowRight />
                        </span>
                    </span>: null}
                </div>
            </CardDeckContainer>
        </CardDeckProvider>
    );
});

export default CardDeck;